// src/pages/ReviewAnalyze.js
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
    Radio,
    Space, Row, Col, Textarea, MessagePlugin
} from 'tdesign-react';
import {
    startAnalysisApi,
    getAnalysisProgressApi,
    postTestAnalysisApi,
    stopAnalysisApi,
    getKvApi,
    createKvApi,
    updateKvApi,
} from '../api/api';

import AnalysisCommentTable from '../components/AnalysisCommentTable';
import AnalysisTaskList from '../components/AnalysisTaskList';
import AnalysisHeader from '../components/AnalysisHeader';
import ReviewReport from './ReviewReport';
import {postChatModel} from "../api/modelService";

const ReviewAnalyze = ({tasks, selectedTask, fetchTasks, SelectedReply, userSubscribeInfo}) => {

    // 基础状态管理
    const [taskList, setTaskList] = useState((() => {
        try {
            const storedData = localStorage.getItem('tasks');
            return storedData ? JSON.parse(storedData) : tasks;
        } catch (error) {
            console.error(`Error accessing localStorage:`, error);
            return tasks;
        }
    })());

    const [inputValues, setInputValues] = useState((() => {
        try {
            const storedData = localStorage.getItem('inputValues');
            return storedData ? JSON.parse(storedData) : {};
        } catch (error) {
            console.error(`Error accessing localStorage:`, error);
            return {};
        }
    })());

    const [currentTaskId, setCurrentTaskId] = useState(() => {
        try {
            const storedTask = localStorage.getItem('selectedTask');
            const parsedTask = storedTask ? JSON.parse(storedTask) : null;
            return selectedTask?.task_id ?? parsedTask?.task_id ?? (taskList.length > 0 ? taskList[0].task_id : null);
        } catch (error) {
            console.error('Error parsing task_id from localStorage:', error);
            return selectedTask?.task_id ?? (taskList.length > 0 ? taskList[0].task_id : null);
        }
    });
    
    const [currentPlatform, setCurrentPlatform] = useState(() => {
        try {
            const storedTask = localStorage.getItem('selectedTask');
            const parsedTask = storedTask ? JSON.parse(storedTask) : null;
            return selectedTask?.platform ?? parsedTask?.platform ?? (taskList.length > 0 ? taskList[0].platform : null);
        } catch (error) {
            console.error('Error parsing platform from localStorage:', error);
            return selectedTask?.platform ?? (taskList.length > 0 ? taskList[0].platform : null);
        }
    });
    
    const [currentKeyWord, setCurrentKeyword] = useState(() => {
        try {
            const storedTask = localStorage.getItem('selectedTask');
            const parsedTask = storedTask ? JSON.parse(storedTask) : null;
            return selectedTask?.keyword ?? parsedTask?.keyword ?? (taskList.length > 0 ? taskList[0].keyword : null);
        } catch (error) {
            console.error('Error parsing keyword from localStorage:', error);
            return selectedTask?.keyword ?? (taskList.length > 0 ? taskList[0].keyword : null);
        }
    });
    const [analysisState, setAnalysisState] = useState("initial");
    const [analysisAble, setAnalysisAble] = useState(true);
    const [activeMode, setActiveMode] = useState("1");
    const [analysisResult, setAnalysisResult] = useState("column");
    const [templateContent, setTemplateContent] = useState({content1: '', content2: ''});
    const [chartOptions, setChartOptions] = useState({});
    const [commentsTotal, setCommentsTotal] = useState(0);
    const [PieData, setPieData] = useState();
    const [pieDataLoading, setPieDataLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(false);
    const platform = useRef('');

    const platformMap = {
        dy: '抖音',
        xhs: '小红书'
    };

    const [allComments, setAllComments] = useState([]);

    // 更新当前进度状态
    const updateProgress = useCallback(async (taskId, forceUpdate = false) => {
        if (!taskId) return;
        
        // 如果当前状态是完成状态且不是强制更新，则不调用接口
        if (analysisState === "finish" && !forceUpdate) {
            return;
        }

        try {
            const data = await getAnalysisProgressApi(taskId);
            if (data.msg === "success") {
                console.log('Progress data:', data.data); // 输出进度数据便于调试
                let stateChanged = false;
                
                // 检查是否已完成（state=3 或 num=sum 且不为 0）
                const isFinished = data.data.state === 3 || 
                                  (data.data.sum !== 0 && data.data.num === data.data.sum);
                
                if(data.data.state === 1 && analysisState !== "initial"){
                    setAnalysisState("initial");
                    stateChanged = true;
                }
                else if(data.data.state === 2 && analysisState !== "running"){
                    setAnalysisState("running");
                    stateChanged = true;
                }
                else if(isFinished && analysisState !== "finish"){
                    setAnalysisState("finish");
                    stateChanged = true;
                    // 如果状态变为完成，立即在localStorage中更新任务状态
                    try {
                        const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
                        const updatedTasks = tasks.map(task => {
                            if (task.task_id === taskId) {
                                return { ...task, analysis_state: "finish" };
                            }
                            return task;
                        });
                        localStorage.setItem('tasks', JSON.stringify(updatedTasks));
                    } catch (e) {
                        console.error('Error updating task status in localStorage:', e);
                    }
                }
                
                setCommentsTotal(data.data.sum);
                
                // 只在状态发生变化且状态变为完成时才获取任务列表
                if (stateChanged && isFinished) {
                    // 使用并发控制变量避免重复调用
                    if (!window._isFetchingTasks) {
                        window._isFetchingTasks = true;
                        await fetchTasks();
                        window._isFetchingTasks = false;
                        
                        // 使用回调函数形式更新状态，避免使用过时的数据
                        setTaskList(prev => {
                            try {
                                const newTasks = JSON.parse(localStorage.getItem('tasks'));
                                return newTasks || prev;
                            } catch (e) {
                                console.error('Error parsing tasks from localStorage:', e);
                                return prev;
                            }
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Failed to update progress:', error);
        }
    }, [fetchTasks, setTaskList, analysisState]);

    // 处理输入改变
    const handleInputChange = useCallback((taskId, value) => {
        setInputValues(prev => {
            const newValues = {...prev, [taskId]: value};
            localStorage.setItem("inputValues", JSON.stringify(newValues));
            return newValues;
        });
    }, []);

    // 更新模板内容
    const updateTemplateContent = async (taskId) => {
        const jsonValue = {
            content1: templateContent.content1,
            content2: templateContent.content2
        };

        const back_data = {
            task_id: taskId,
            json_key: "template",
            json_value: JSON.stringify(jsonValue)
        };

        try {
            const response = await getKvApi(taskId, "template");
            if (response.code === 200) {
                if (response.data.length === 0) {
                    await createKvApi(back_data);
                } else {
                    await updateKvApi(back_data);
                }
            }
        } catch (error) {
            console.error('Failed to update template:', error);
        }
    };

    // 开始分析
    const startAnalyze = async (taskId, commentId = null) => {
        await updateTemplateContent(taskId);
        setAnalysisAble(false);

        const storedTemplate = localStorage.getItem(`${currentTaskId}-template`);
        const currentTemplate = storedTemplate ? JSON.parse(storedTemplate) : templateContent;
        const keyword = inputValues[taskId] || currentKeyWord;

        const analysisRequest = activeMode === "1" ?
`背景：${currentTemplate.content1 ? "我们的服务介绍是：" + currentTemplate.content1 : ""}
${currentTemplate.content2 ? "我想要的客户描述：" + currentTemplate.content2 : ""}
你要分析抖音以及小红书平台上关于《${keyword}》搜索后呈现相关内容下方的评论，内容都是跟关键词对应相关的
你的任务：
1. 分析关键词背后对应的产品、服务、行业
2. 判断后面提供的评论是否是对这个关键词对应的营销视频/笔记内容感兴趣的客户
3. 对这个客户进行评级：
高意向：想了解我们的服务价格、或想学习、合作、加盟、购买以及对产品/服务有疑问或比较纠结的潜在客户
低意向：需求不确定、对产品/服务无意向或批评的客户、提供同样产品/服务的同行，直接意向判定为无意向` :
`背景：现在要帮助${keyword}相关的公司来分析用户在${currentPlatform}平台上关于${keyword}相关关键词内容下面的评论
任务：分析这个评论的用户是否已经或可能成为相关服务的客户，并且分析评论中用户的情绪、提及相关产品/服务的情况、对相关产品/服务的满意点、不满点、疑问点`;

        const outputFields = activeMode === "1" ? [
            {
                key: "意向客户",
                explanation: `用户是否为有明确意向了解/购买${keyword}这个产品/服务的高意向客户（有明确意向填是，其他都填否)`
            },
            {
                key: "分析理由",
                explanation: "20字内简要说明这么分析的理由"
            }
        ] : [
            {
                key: "意向客户",
                explanation: `可能有意向：不限于想了解我们的服务价格、或想学习、合作、加盟、购买、聊天相关产品/服务/公司，对产品/服务有疑问或比较纠结的客户，填是
                无意向：对产品/服务无意向或批评的客户以及提供同样产品/服务的同行直接意向判定为无意向，填否`
            },
            {
                key: "情绪分析",
                explanation: "分析用户的情绪，填写选项：正向、负向、中性"
            },
            {
                key: "提及产品",
                explanation: `用户这条评论是否是围绕着${keyword}相关的产品/品牌或服务进行讨论（是、否、不确定）`
            },
            {
                key: "满意点",
                explanation: "评论中对本产品/品牌/服务的满意点，没有就填写没有"
            },
            {
                key: "不满点",
                explanation: "评论中对本产品/品牌/服务的不满点，没有就填写没有"
            },
            {
                key: "疑问点",
                explanation: "评论中对本产品/品牌/服务的疑问点，没有就填写没有"
            }
        ];

        try {
            const raw = {
                comment_id: commentId,
                task_id: taskId,
                platform: platform.current,
                analysis_request: analysisRequest,
                output_fields: outputFields
            };

            if (commentId) {
                await postTestAnalysisApi(raw);
            } else {
                await startAnalysisApi(raw);
            }

            await updateProgress(taskId);
            setAnalysisAble(true);
            setAnalysisState("running");
        } catch (error) {
            console.error('Failed to start analysis:', error);
            MessagePlugin.error('开始分析失败');
            setAnalysisAble(true);
        }
    };

    // 停止分析
    const stopAnalyze = async (taskId) => {
        setAnalysisAble(false);
        try {
            await stopAnalysisApi({task_id: taskId});
            await updateProgress(taskId);
        } catch (error) {
            console.error('Failed to stop analysis:', error);
            MessagePlugin.error('停止分析失败');
        } finally {
            setTimeout(() => setAnalysisAble(true), 2000);
        }
    };

    // 处理分析操作
    const handleAnalysis = async (taskId, commentId = null) => {
        if (analysisState === 'initial') {
            await startAnalyze(taskId, commentId);
        } else if (analysisState === 'running') {
            await stopAnalyze(taskId);
        }
        await fetchTasks();
        setTaskList(JSON.parse(localStorage.getItem('tasks')));
    };

    // 处理任务点击
    const handleTaskClick = async (item) => {
        if (!item || !item.task_id) return;
        localStorage.setItem('selectedTask', JSON.stringify(item));
        setCurrentTaskId(item.task_id);
        setCurrentPlatform(item.platform);
        setCurrentKeyword(item.keyword);
        
        // 首先检查任务是否已完成（从 localStorage 中）
        const isFinished = item.analysis_state === "finish" || checkTaskFinished(item.task_id);
        
        if (isFinished) {
            // 如果任务已完成，设置状态为完成并强制更新一次获取最新数据
            setAnalysisState("finish");
            await updateProgress(item.task_id, true);
        } else {
            // 如果任务未完成，直接从 API 检查任务状态
            try {
                const apiFinished = await checkTaskStatusFromAPI(item.task_id);
                if (apiFinished) {
                    // API 显示任务已完成，设置状态为完成
                    setAnalysisState("finish");
                } else {
                    // API 显示任务未完成，设置状态并更新进度
                    setAnalysisState(item.analysis_state);
                    await updateProgress(item.task_id);
                }
            } catch (error) {
                console.error('Error checking task status during task switch:', error);
                // 出错时使用原始状态并更新进度
                setAnalysisState(item.analysis_state);
                await updateProgress(item.task_id);
            }
        }
        
        // 已完成任务切换
    };

    // 处理模板输入改变
    const handleInputChangeT1 = (value) => {
        setTemplateContent(prev => {
            const newTemplate = {...prev, content1: value};
            localStorage.setItem(`${currentTaskId}-template`, JSON.stringify(newTemplate));
            return newTemplate;
        });
    };

    const handleInputChangeT2 = (value) => {
        setTemplateContent(prev => {
            const newTemplate = {...prev, content2: value};
            localStorage.setItem(`${currentTaskId}-template`, JSON.stringify(newTemplate));
            return newTemplate;
        });
    };

    const handleGotoReply = () => {
        if (currentTaskId) {
            SelectedReply("c", currentTaskId);
        } else {
            MessagePlugin.error('任务ID不存在');
        }
    };

    // 初始加载模板数据的标记
    const isFirstMount = useRef(true);
    
    // 检查任务是否已完成的函数（同步）
    const checkTaskFinished = useCallback((taskId) => {
        // 首先检查当前 React 状态
        if (analysisState === "finish") return true;
        
        // 然后检查 localStorage 中的任务状态
        try {
            const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
            const task = tasks.find(t => t.task_id === taskId);
            // 检查任务状态是否为完成
            return task && task.analysis_state === "finish";
        } catch (e) {
            console.error('Error checking task status from localStorage:', e);
            return analysisState === "finish"; // 如果出错，回退到使用 React 状态
        }
    }, [analysisState]);
    
    // 异步检查任务状态，直接从 API 获取
    const checkTaskStatusFromAPI = useCallback(async (taskId) => {
        try {
            const data = await getAnalysisProgressApi(taskId);
            if (data.msg === "success") {
                // state=3 或者 num=sum 且不为 0 表示任务已完成
                const isFinished = data.data.state === 3 || 
                                  (data.data.sum !== 0 && data.data.num === data.data.sum);
                                  
                // 如果任务已完成，更新 localStorage 中的状态
                if (isFinished && analysisState !== "finish") {
                    try {
                        const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
                        const updatedTasks = tasks.map(task => {
                            if (task.task_id === taskId) {
                                return { ...task, analysis_state: "finish" };
                            }
                            return task;
                        });
                        localStorage.setItem('tasks', JSON.stringify(updatedTasks));
                        setAnalysisState("finish");
                    } catch (e) {
                        console.error('Error updating task status in localStorage:', e);
                    }
                }
                
                return isFinished;
            }
            return false;
        } catch (error) {
            console.error('Error checking task status from API:', error);
            return false;
        }
    }, [analysisState]);
    
    // 初始加载模板数据
    useEffect(() => {
        const loadTemplateData = async () => {
            if (!currentTaskId) return;
            
            // 如果currentKeyWord为空但currentTaskId有值，尝试从任务列表中获取关键词
            if (!currentKeyWord && taskList.length > 0) {
                const currentTask = taskList.find(task => task.task_id === currentTaskId);
                if (currentTask && currentTask.keyword) {
                    setCurrentKeyword(currentTask.keyword);
                }
            }

            try {
                const storedData = localStorage.getItem(`${currentTaskId}-template`);
                if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    if ('content1' in parsedData && 'content2' in parsedData) {
                        setTemplateContent(parsedData);
                        return;
                    }
                }

                // 只有当关键词存在时才调用模型接口
                if (currentKeyWord) {
                    setLoading(true);
                    const data = await postChatModel(currentKeyWord, "template");
                    if (data.code === 0) {
                        const jsonData = JSON.parse(data.messages[0].content);
                        const newTemplate = {
                            content1: jsonData['我提供的服务介绍'],
                            content2: jsonData['我想要的客户描述']
                        };
                        setTemplateContent(newTemplate);
                        localStorage.setItem(`${currentTaskId}-template`, JSON.stringify(newTemplate));
                    }
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error loading template:', error);
                MessagePlugin.error("模板加载失败");
                setLoading(false);
            }
        };

        // 只在组件首次渲染或当前任务ID变化时加载模板数据
        if (isFirstMount.current || window._lastTaskId !== currentTaskId) {
            loadTemplateData();
            window._lastTaskId = currentTaskId;
            
            // 检查任务是否已完成
            const isTaskFinished = checkTaskFinished(currentTaskId);
            
            // 如果任务未完成，才调用更新进度接口
            if (currentTaskId && !isTaskFinished) {
                // 使用强制更新参数，确保首次加载时能获取最新状态
                setTimeout(() => {
                    updateProgress(currentTaskId, true);
                }, 100); // 添加小延迟避免并发请求
            } else if (currentTaskId && isTaskFinished) {
                // 如果任务已完成，确保状态正确设置为完成
                setAnalysisState("finish");
            }
        }
        
        // 组件首次渲染后将标记设置为已渲染
        isFirstMount.current = false;
        
        return () => {
            // 清除并发控制标记
            window._isFetchingTasks = false;
        };
    }, [currentTaskId, currentKeyWord, taskList, updateProgress, analysisState, checkTaskFinished]);

    // 更新平台引用
    useEffect(() => {
        if (currentPlatform) {
            platform.current = currentPlatform;
        } else if (currentTaskId && taskList.length > 0) {
            // 如果平台为空但有任务ID，尝试从任务列表中获取平台信息
            const currentTask = taskList.find(task => task.task_id === currentTaskId);
            if (currentTask && currentTask.platform) {
                setCurrentPlatform(currentTask.platform);
            }
        }
    }, [currentPlatform, currentTaskId, taskList]);
    
    // 自动填充关键词到输入框
    useEffect(() => {
        if (currentKeyWord && currentTaskId) {
            // 只在inputValues中没有值或值为空字符串时填充
            if (!inputValues[currentTaskId] || inputValues[currentTaskId] === '') {
                handleInputChange(currentTaskId, currentKeyWord);
            }
        }
    }, [currentKeyWord, currentTaskId, inputValues, handleInputChange]);

    // 定时更新进度
    useEffect(() => {
        // 如果没有任务ID，不需要设置定时器
        if (!currentTaskId) {
            return;
        }
        
        // 首次加载时直接从 API 检查任务状态
        const initialCheck = async () => {
            const isFinished = await checkTaskStatusFromAPI(currentTaskId);
            if (isFinished) {
                // 如果任务已完成，不需要设置定时器
                return true;
            }
            return false;
        };
        
        // 如果任务已完成（从 localStorage 检查），不设置定时器
        if (checkTaskFinished(currentTaskId)) {
            // 确保状态正确设置为完成
            if (analysisState !== "finish") {
                setAnalysisState("finish");
            }
            return;
        }
        
        // 使用防抖变量避免重复调用
        let isUpdating = false;
        let intervalId;
        
        // 首先从 API 检查任务状态
        initialCheck().then(isFinished => {
            if (isFinished) {
                return; // 如果任务已完成，不设置定时器
            }
            
            // 当有任务ID且不是完成状态时设置检查间隔
            intervalId = setInterval(async () => {
                // 每次调用前再次检查任务是否完成
                if (checkTaskFinished(currentTaskId) || isUpdating) {
                    if (checkTaskFinished(currentTaskId)) {
                        // 如果任务已完成，清除定时器并确保状态正确
                        if (analysisState !== "finish") {
                            setAnalysisState("finish");
                        }
                        clearInterval(intervalId);
                    }
                    return;
                }
                
                // 设置防抖标记
                isUpdating = true;
                
                try {
                    // 使用直接从 API 检查任务状态的函数
                    const isFinished = await checkTaskStatusFromAPI(currentTaskId);
                    if (isFinished) {
                        clearInterval(intervalId);
                    }
                } finally {
                    // 无论成功失败都重置防抖标记
                    isUpdating = false;
                }
            }, 3000);
        });
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [currentTaskId, analysisState, checkTaskFinished, checkTaskStatusFromAPI]);

    // 处理报表数据
    useEffect(() => {
        if (activeMode === '2' && analysisState === 'finish') {
            const PieDataList = localStorage.getItem(`${currentTaskId}-PieData`);
            if (PieDataList) {
                setPieData(JSON.parse(PieDataList));
            } else {
                setPieDataLoading(true);
            }
        }
    }, [activeMode, analysisState, currentTaskId]);

    useEffect(() => {
        if (chartOptions.title === "emotion") {
            setAnalysisResult("column");
        }
    }, [chartOptions]);

    return (
        <div style={{display: 'flex', width: "100%"}}>
            <AnalysisTaskList
                mode="analyze"
                taskList={taskList}
                currentTaskId={currentTaskId}
                onTaskClick={handleTaskClick}
                platformMap={platformMap}
            />
            <Space direction="vertical" style={{
                marginLeft: "10px",
                height: "94vh",
                overflowX: "hidden",  // 隐藏水平滚动条
                overflowY: "auto",
                paddingLeft: "5px",
            }}>
                <AnalysisHeader
                    commentsTotal={commentsTotal}
                    currentKeyWord={currentKeyWord}
                    currentPlatform={currentPlatform}
                    inputValues={inputValues}
                    currentTaskId={currentTaskId}
                    handleInputChange={handleInputChange}
                    progress={progress}
                    handleAnalysis={handleAnalysis}
                    startAnalyze={startAnalyze}
                    analysisState={analysisState}
                    analysisAble={analysisAble}
                    activeMode={activeMode}
                    taskList={taskList}
                    setTaskList={setTaskList}
                    SelectedReply={handleGotoReply}  // 传递处理函数
                />
                <Space style={{marginBottom: '10px'}}>
                    <Space direction='vertical'>
                        <div>我的服务介绍</div>
                        <Textarea
                            placeholder="例如：我是一家美容机构，提供美容服务"
                            autosize={{minRows: 3}}
                            value={templateContent.content1}
                            onChange={handleInputChangeT1}
                            style={{width: '400px'}}
                            disabled={analysisState === 'finish'}
                        />
                    </Space>
                    <Space direction='vertical'>
                        <div>我想要的客户</div>
                        <Textarea
                            placeholder="例如：我想要有美容需求的客户"
                            autosize={{minRows: 3}}
                            value={templateContent.content2}
                            onChange={handleInputChangeT2}
                            style={{width: '400px'}}
                            disabled={analysisState === 'finish'}
                        />
                    </Space>
                </Space>
                <Row>
                    {analysisState === 'finish' && activeMode === '2' && (
                        <Col flex="none">
                            <Radio.Group
                                style={{marginRight: "15px"}}
                                size="medium"
                                value={analysisResult}
                                onChange={(v) => setAnalysisResult(v)}
                                variant='primary-filled'
                            >
                                <Radio.Button value="column" className="custom-radio-button">分析详情</Radio.Button>
                                <Radio.Button value="report" className="custom-radio-button">分析报告</Radio.Button>
                            </Radio.Group>
                        </Col>
                    )}
                    {/* 获客模式和品牌分析切换按钮已隐藏 */}
                </Row>
                {analysisResult === 'column' && <AnalysisCommentTable
                    taskId={currentTaskId}
                    platform={platform}
                    userSubscribeInfo={userSubscribeInfo}
                    activeMode={activeMode}
                    setAllComments={setAllComments}
                />
                }
                {analysisResult === 'report' &&
                    <ReviewReport taskId={currentTaskId} commentList={allComments} commentsTotal={commentsTotal}
                                  PieData={PieData}
                                  pieDataLoading={pieDataLoading}
                                  setChartOptions={setChartOptions}
                    />
                }
            </Space>
        </div>
    );
};

export default ReviewAnalyze;
